// libs
import React, { memo, useEffect } from 'react';
import { useParams } from 'react-router';
import isEmpty from 'lodash/isEmpty';

// components
import Widgets from 'shared/layouts/DetailsPage/components/Widgets';
import WidgetPlaceholder from './components/WidgetPlaceholder';

// defs
import { IWidgetObject } from 'shared/layouts/DetailsPage/components/Widgets/types';

// redux actions
import { fetchWidgetData as _fetchWidgetData } from 'shared/store/widgets/services';

// hook
import useWidgetLoader from './useWidgetLoader';

export interface IWidgetLoaderProps {
  isWidgetFetching: boolean;
  productIDFromWidgetStore: string;
  productID: string;
  widgetData: IWidgetObject;
  isMobile?: boolean;
  fetchWidgetData: typeof _fetchWidgetData;
  isFailed: boolean;
}

function WidgetLoader({
  isWidgetFetching,
  widgetData,
  productIDFromWidgetStore,
  productID,
  isMobile = false,
  fetchWidgetData,
  isFailed,
}: IWidgetLoaderProps) {
  const { id: routeID = '' } = useParams<{ id: string }>();

  const { transformedWidgetData } = useWidgetLoader({ isMobile });
  const apiConfig = transformedWidgetData;
  useEffect(() => {
    if (isFailed || isMobile) {
      return;
    }

    // don't fetch widget if details data is stale
    if (productID !== routeID) {
      return;
    }

    if (isWidgetFetching) {
      return;
    }

    // don't fetch if existing redux data is for same productID
    if (productID === productIDFromWidgetStore) {
      return;
    }

    fetchWidgetData({
      productID,
      apiConfig,
    });
  }, [
    routeID,
    productIDFromWidgetStore,
    productID,
    isWidgetFetching,
    fetchWidgetData,
    isFailed,
    apiConfig,
    isMobile,
  ]);

  if (isWidgetFetching === false && isEmpty(widgetData)) {
    return <WidgetPlaceholder />;
  }

  const showPlaceholder =
    isWidgetFetching ||
    routeID !== productIDFromWidgetStore ||
    productID !== productIDFromWidgetStore;

  if (showPlaceholder) {
    return <WidgetPlaceholder />;
  }

  return <Widgets isMobile={isMobile} fallback={<WidgetPlaceholder />} />;
}

export default memo(WidgetLoader);
